import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAutomationClient } from '../providers/AutomationProvider'
import type {
  TicketsListParams,
  CreateTicketParams,
  UpdateTicketParams,
  CreateTicketCommentParams,
  TicketStatus,
} from '../types'

/**
 * Query key factory for tickets-related queries
 */
export const ticketsKeys = {
  all: ['tickets'] as const,
  lists: () => [...ticketsKeys.all, 'list'] as const,
  list: (params?: TicketsListParams) => [...ticketsKeys.lists(), params] as const,
  details: () => [...ticketsKeys.all, 'detail'] as const,
  detail: (id: string) => [...ticketsKeys.details(), id] as const,
  activity: (id: string) => [...ticketsKeys.detail(id), 'activity'] as const,
}

/**
 * Hook to fetch a paginated list of tickets
 */
export function useTickets(params?: TicketsListParams) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: ticketsKeys.list(params),
    queryFn: () => client.tickets.list(params),
  })
}

/**
 * Hook to fetch a single ticket with details
 */
export function useTicket(id: string, options?: { enabled?: boolean }) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: ticketsKeys.detail(id),
    queryFn: () => client.tickets.get(id),
    enabled: options?.enabled !== false && !!id,
  })
}

/**
 * Hook to create a new ticket
 */
export function useCreateTicket() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateTicketParams) => client.tickets.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ticketsKeys.lists() })
    },
  })
}

/**
 * Hook to update a ticket
 */
export function useUpdateTicket() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTicketParams }) =>
      client.tickets.update(id, data),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketsKeys.lists() })
      queryClient.setQueryData(ticketsKeys.detail(id), data)
    },
  })
}

/**
 * Hook to update a ticket's status
 */
export function useUpdateTicketStatus() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: TicketStatus }) =>
      client.tickets.updateStatus(id, status),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketsKeys.lists() })
      queryClient.setQueryData(ticketsKeys.detail(id), data)
    },
  })
}

/**
 * Hook to assign a ticket
 */
export function useAssignTicket() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, assigneeId }: { id: string; assigneeId: string | null }) =>
      client.tickets.assign(id, assigneeId),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketsKeys.lists() })
      queryClient.setQueryData(ticketsKeys.detail(id), data)
    },
  })
}

/**
 * Hook to add a comment to a ticket
 */
export function useAddTicketComment() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateTicketCommentParams }) =>
      client.tickets.addComment(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ticketsKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: ticketsKeys.activity(id) })
    },
  })
}

/**
 * Hook to fetch ticket activity
 */
export function useTicketActivity(id: string, options?: { enabled?: boolean }) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: ticketsKeys.activity(id),
    queryFn: () => client.tickets.getActivity(id),
    enabled: options?.enabled !== false && !!id,
  })
}

/**
 * Hook to link an error to a ticket
 */
export function useLinkErrorToTicket() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ ticketId, errorGroupId }: { ticketId: string; errorGroupId: string }) =>
      client.tickets.linkError(ticketId, errorGroupId),
    onSuccess: (_, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: ticketsKeys.detail(ticketId) })
      queryClient.invalidateQueries({ queryKey: ticketsKeys.activity(ticketId) })
    },
  })
}

/**
 * Hook to link a test case to a ticket
 */
export function useLinkTestCaseToTicket() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ ticketId, testCaseId }: { ticketId: string; testCaseId: string }) =>
      client.tickets.linkTestCase(ticketId, testCaseId),
    onSuccess: (_, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: ticketsKeys.detail(ticketId) })
      queryClient.invalidateQueries({ queryKey: ticketsKeys.activity(ticketId) })
    },
  })
}

/**
 * Hook to sync a ticket to Linear
 */
export function useSyncTicketToLinear() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => client.tickets.syncToLinear(id),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: ticketsKeys.lists() })
      queryClient.setQueryData(ticketsKeys.detail(id), data)
      queryClient.invalidateQueries({ queryKey: ticketsKeys.activity(id) })
    },
  })
}
